import { useCallback, useEffect, useRef, useState } from "react";

// Generic "Load More" pagination over a fetchPage(cursor) -> { items, hasMore, nextCursor? }
// function. Re-runs from the initial cursor whenever `deps` changes (e.g. a
// search term or filter), so callers don't have to hand-roll reset logic.
//
// cursor defaults to a plain page number (1, 2, 3...) and nextCursor defaults
// to cursor + 1 if the fetcher doesn't return one — that covers the common
// "one API page per Load More click" case. A fetcher can instead return an
// explicit nextCursor to walk forward through more than one real page in a
// single logical "page" (see LiveOrders.js, which needs this: the orders
// endpoint has no server-side "live only" filter, so one raw page can come
// back with few or zero matching orders after client-side filtering — without
// this, "Load More" would look like it barely does anything most of the time).
export default function usePaginatedList(fetchPage, deps = [], initialCursor = 1) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(initialCursor);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // Always holds the latest fetchPage (it closes over things like a search
  // term, so it gets a new identity on every relevant change) — read via this
  // ref rather than closed over directly by loadPage below, which must stay a
  // stable function (see why on its own useCallback) but still needs to call
  // whatever fetchPage is current, not whatever it was on the first render.
  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });

  const loadPage = useCallback(
    async (thisCursor, append) => {
      append ? setLoadingMore(true) : setLoading(true);
      setError("");
      try {
        const { items: newItems, hasMore: more, nextCursor } = await fetchPageRef.current(thisCursor);
        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setHasMore(more);
        setCursor(nextCursor !== undefined ? nextCursor : thisCursor + 1);
      } catch (err) {
        setError(err.message || "Failed to load");
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    // Deliberately stable (no deps): it must be safe to call at any time (from
    // the deps-driven effect below, or from loadMore/reload) without itself
    // triggering a "reset to page 1" — that's controlled by `deps` alone.
    // fetchPageRef.current is always fresh, so this doesn't go stale.
    []
  );

  useEffect(() => {
    loadPage(initialCursor, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) loadPage(cursor, true);
  };

  const reload = () => loadPage(initialCursor, false);

  return { items, loading, loadingMore, hasMore, error, loadMore, reload };
}
